import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { Plus, MessageSquare, Calendar, MapPin, Trash2, Eye, Pencil } from 'lucide-react';

/**
 * InterviewsTab — full-page "Record Interview" flow now lives at
 * `/cases/:caseId/interviews/new` (InterviewFormPage). Dialog removed.
 */
const InterviewsTab = ({
  caseId,
  interviews,
  canEdit,
  isAdmin,
  token,
  onRefresh,
  onViewItem,
  onEditItem,
  onDeleteItem,
}) => {
  const navigate = useNavigate();

  const handleDeleteInterview = async (interviewId) => {
    if (onDeleteItem) return onDeleteItem('interview', interviewId, 'Delete this interview?');
    if (!window.confirm('Delete this interview?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/interviews/${interviewId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      onRefresh();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
            onClick={() => navigate(`/cases/${caseId}/interviews/new`)} data-testid="add-interview-btn">
            <Plus className="w-4 h-4 mr-2" /> Record Interview
          </Button>
        </div>
      )}
      {interviews.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-8 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500">No interviews recorded yet</p>
          </CardContent>
        </Card>
      ) : (
        interviews.map(interview => (
          <Card key={interview.id} className="bg-white border-slate-200 hover:border-[#0F172A]/30 transition-colors" data-testid={`interview-card-${interview.id}`}>
            <CardContent className="p-4">
              <button onClick={() => onViewItem(interview, 'interview')} className="text-left w-full">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div>
                    <h3 className="text-slate-900 font-semibold">{interview.interviewee_name}</h3>
                    {interview.relationship_to_case && (
                      <p className="text-xs text-slate-500">{interview.relationship_to_case}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{interview.interview_date}</span>
                  {interview.interview_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{interview.interview_location}</span>}
                </div>
                {interview.summary && <p className="text-slate-700 text-sm">{interview.summary}</p>}
                {interview.observations && <p className="text-slate-500 text-xs mt-2">Observations: {interview.observations}</p>}
                <p className="text-xs text-slate-500 mt-2">Conducted by {interview.interviewer_name}</p>
              </button>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  onClick={() => onViewItem(interview, 'interview')} data-testid={`interview-view-${interview.id}`} title="View details">
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
                {canEdit && (
                  <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={() => (onEditItem ? onEditItem(interview, 'interview') : onViewItem(interview, 'interview'))}
                    data-testid={`interview-edit-${interview.id}`} title="Edit interview">
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteInterview(interview.id)} data-testid={`interview-delete-${interview.id}`} title="Delete interview">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default InterviewsTab;
